<script setup lang="ts">
import { ref, computed, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { mdiCloudUpload, mdiTrayArrowDown } from '@mdi/js'

interface Props {
  accept?: string
  multiple?: boolean
  disabled?: boolean
  maxSizeMB?: number | null
  disableDrag?: boolean
}

interface Emits {
  (e: 'files-selected', files: File[]): void
  (e: 'error', message: string): void
}

const props = withDefaults(defineProps<Props>(), {
  accept: '*/*',
  multiple: true,
  disabled: false,
  maxSizeMB: undefined,
  disableDrag: false,
})

const emit = defineEmits<Emits>()

const { t } = useI18n()

const fileInput = ref<HTMLInputElement | null>(null)
const isDragOver = ref(false)
const isOpeningFilePicker = ref(false)

const hasMaxSize = computed(() => props.maxSizeMB != null && props.maxSizeMB > 0)
const maxSizeBytes = computed(() => hasMaxSize.value ? props.maxSizeMB! * 1024 * 1024 : Infinity)

function handleWindowFocus() {
  setTimeout(() => {
    isOpeningFilePicker.value = false
    window.removeEventListener('focus', handleWindowFocus)
  }, 300)
}

function handleClick(event: MouseEvent) {
  event.stopPropagation()
  if (!props.disabled && !isOpeningFilePicker.value) {
    isOpeningFilePicker.value = true
    window.addEventListener('focus', handleWindowFocus)
    fileInput.value?.click()
  }
}

onUnmounted(() => {
  window.removeEventListener('focus', handleWindowFocus)
})

function handleDragEnter(event: DragEvent) {
  if (props.disableDrag) return
  event.preventDefault()
  event.stopPropagation()
  if (!props.disabled) {
    isDragOver.value = true
  }
}

function handleDragLeave(event: DragEvent) {
  if (props.disableDrag) return
  event.preventDefault()
  event.stopPropagation()
  isDragOver.value = false
}

function handleDragOver(event: DragEvent) {
  if (props.disableDrag) return
  event.preventDefault()
  event.stopPropagation()
}

function handleDrop(event: DragEvent) {
  if (props.disableDrag) return
  event.preventDefault()
  event.stopPropagation()
  isDragOver.value = false

  if (props.disabled) return

  const files = Array.from(event.dataTransfer?.files ?? [])
  processFiles(files)
}

function handleFileChange(event: Event) {
  event.stopPropagation()
  isOpeningFilePicker.value = false
  const input = event.target as HTMLInputElement
  const files = Array.from(input.files ?? [])
  processFiles(files)
  input.value = ''
}

function processFiles(files: File[]) {
  const validFiles: File[] = []

  for (const file of files) {
    if (hasMaxSize.value && file.size > maxSizeBytes.value) {
      emit('error', t('assets.upload.errors.fileTooLarge', { size: props.maxSizeMB }))
      continue
    }
    validFiles.push(file)
  }

  if (validFiles.length > 0) {
    emit('files-selected', validFiles)
  }
}
</script>

<template>
  <div
    class="upload-dropzone"
    :class="{
      'upload-dropzone--drag-over': isDragOver,
      'upload-dropzone--disabled': disabled,
      'upload-dropzone--loading': isOpeningFilePicker,
    }"
    @click="handleClick"
    @dragenter="handleDragEnter"
    @dragleave="handleDragLeave"
    @dragover="handleDragOver"
    @drop="handleDrop"
  >
    <input
      ref="fileInput"
      type="file"
      :accept="accept"
      :multiple="multiple"
      :disabled="disabled"
      class="upload-dropzone__input"
      @change="handleFileChange"
    />

    <div class="upload-dropzone__content">
      <div class="upload-dropzone__icon-wrapper">
        <v-progress-circular
          v-if="isOpeningFilePicker"
          indeterminate
          size="32"
          width="2"
          color="primary"
        />
        <v-icon
          v-else
          :icon="isDragOver ? mdiTrayArrowDown : mdiCloudUpload"
          :size="32"
          class="upload-dropzone__icon"
        />
      </div>

      <div class="upload-dropzone__text">
        <span class="upload-dropzone__title">
          <template v-if="isDragOver">
            {{ t('assets.upload.dropZone.dropHere') }}
          </template>
          <template v-else>
            {{ t('assets.upload.dropZone.clickOrDrag') }}
          </template>
        </span>
        <span v-if="hasMaxSize && !isDragOver" class="upload-dropzone__hint">
          {{ t('assets.upload.dropZone.helper', { size: maxSizeMB }) }}
        </span>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.upload-dropzone {
  position: relative;
  padding: 20px;
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  // Glassmorphism effect - light
  background: rgba(var(--v-theme-primary), 0.03);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(var(--v-theme-primary), 0.1);
  box-shadow:
    0 2px 12px rgba(0, 0, 0, 0.02),
    inset 0 1px 1px rgba(255, 255, 255, 0.4);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 16px;
    padding: 1px;
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.2),
      rgba(255, 255, 255, 0.05)
    );
    mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    mask-composite: xor;
    -webkit-mask-composite: xor;
    pointer-events: none;
    opacity: 0.5;
    transition: opacity 0.3s ease;
  }

  &:hover:not(&--disabled) {
    background: rgba(var(--v-theme-primary), 0.08);
    border-color: rgba(var(--v-theme-primary), 0.3);
    transform: translateY(-1px);
    box-shadow:
      0 8px 32px rgba(var(--v-theme-primary), 0.12),
      inset 0 1px 1px rgba(255, 255, 255, 0.15);

    &::before {
      opacity: 1;
      background: linear-gradient(
        135deg,
        rgba(var(--v-theme-primary), 0.4),
        rgba(var(--v-theme-primary), 0.1)
      );
    }

    .upload-dropzone__icon {
      opacity: 1;
      transform: translateY(-2px);
    }
  }

  &--drag-over {
    background: rgba(var(--v-theme-primary), 0.12);
    border-color: rgb(var(--v-theme-primary));
    transform: scale(1.01);
    box-shadow:
      0 8px 40px rgba(var(--v-theme-primary), 0.2),
      inset 0 0 20px rgba(var(--v-theme-primary), 0.05);

    &::before {
      opacity: 1;
      background: linear-gradient(
        135deg,
        rgba(var(--v-theme-primary), 0.5),
        rgba(var(--v-theme-primary), 0.2)
      );
    }

    .upload-dropzone__icon {
      opacity: 1;
      animation: bounce-in 0.4s ease;
    }

    .upload-dropzone__title {
      color: rgb(var(--v-theme-primary));
    }
  }

  &--disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  &--loading {
    cursor: wait;
  }

  &__input {
    display: none;
  }

  &__content {
    display: flex;
    align-items: center;
    gap: 16px;
    position: relative;
    z-index: 1;
  }

  &__icon-wrapper {
    flex-shrink: 0;
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 12px;
    background: rgba(var(--v-theme-primary), 0.08);
    transition: all 0.3s ease;
  }

  &__icon {
    color: rgb(var(--v-theme-primary));
    opacity: 0.7;
    transition: all 0.3s ease;
  }

  &__text {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__title {
    font-size: 14px;
    font-weight: 500;
    color: rgba(var(--v-theme-on-surface), 0.87);
    transition: color 0.3s ease;
  }

  &__hint {
    font-size: 12px;
    color: rgba(var(--v-theme-on-surface), 0.5);
  }
}

// Dark mode adjustments
:root.v-theme--dark .upload-dropzone {
  background: rgba(var(--v-theme-primary), 0.05);
  border-color: rgba(var(--v-theme-primary), 0.15);
  box-shadow:
    0 2px 12px rgba(0, 0, 0, 0.1),
    inset 0 1px 1px rgba(255, 255, 255, 0.03);
}

@keyframes bounce-in {
  0% {
    transform: scale(0.9);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
}
</style>
